import React, { useRef, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { downloadCsv } from "./adminShared";

const isImage = (f) => f.type.startsWith("image/") || /\.(jpe?g|png|webp|gif)$/i.test(f.name);
const isZip = (f) => f.type === "application/zip" || f.name.toLowerCase().endsWith(".zip");
const collectFiles = (files) => Array.from(files).filter((f) => isImage(f) || isZip(f));

function Results({ results }) {
    const s = results.summary;
    return (
        <div className="mt-6 space-y-2">
            <p className="text-sm">
                <span className="text-brand-olive font-medium">{s.matched} matched</span>
                {s.matched_by_ai > 0 && <span className="text-brand-gold ml-3">{s.matched_by_ai} by AI</span>}
                {s.unmatched > 0 && <span className="text-brand-gold ml-3">· {s.unmatched} unmatched</span>}
                {s.skipped > 0 && <span className="text-brand-mute ml-3">· {s.skipped} skipped</span>}
            </p>
            <div className="max-h-64 overflow-y-auto border border-brand-line rounded-sm text-xs">
                {results.results.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 border-b border-brand-line last:border-0">
                        <span className={r.status === "ok" ? "text-brand-olive" : "text-brand-gold"}>{r.status === "ok" ? "✓" : "?"}</span>
                        <span className="text-brand-cream/80 truncate flex-1">{r.filename}</span>
                        {r.source === "ai-vision" && <span className="text-brand-gold text-[10px] px-1.5 py-0.5 border border-brand-gold/40 rounded-sm">AI {r.confidence}</span>}
                        {r.matched_name && <span className="text-brand-mute truncate">→ {r.matched_name}</span>}
                        {r.status === "unmatched" && <span className="text-brand-mute">no match</span>}
                        {r.reason && <span className="text-brand-mute">{r.reason}</span>}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function BulkUploadPanel({ onDone }) {
    const [results, setResults] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [aiMatch, setAiMatch] = useState(true);
    const imgRef = useRef(null);
    const folderRef = useRef(null);
    const zipRef = useRef(null);

    const submit = async (files) => {
        if (files.length === 0) return;
        setUploading(true);
        setResults(null);
        try {
            const zipFile = files.find(isZip);
            const fd = new FormData();
            if (zipFile) fd.append("file", zipFile);
            else files.forEach((f) => fd.append("files", f));
            const { data } = await client.post(zipFile ? "/admin/upload/zip" : "/admin/upload/bulk", fd, {
                params: { ai_match: aiMatch },
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 600000,
            });
            setResults(data);
            toast.success(`${data.summary.matched} images uploaded`);
            if (onDone) await onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Bulk upload failed");
        } finally {
            setUploading(false);
        }
    };

    const pick = (ref) => (e) => { e.preventDefault(); ref.current?.click(); };

    return (
        <div className="border border-brand-line rounded-sm p-6 bg-brand-charcoal">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h3 className="font-display text-2xl">Bulk image upload</h3>
                    <p className="text-sm text-brand-mute mt-1 max-w-xl">
                        Drop a folder, several images or a .zip. Filenames match item slugs (e.g. cheese-omelette.jpg). Existing images are replaced.
                    </p>
                </div>
                <button onClick={() => downloadCsv("/admin/menu/slugs.csv", "umami-menu-slugs.csv")} className="btn-ghost text-xs" data-testid="bulk-slug-csv">
                    Download slug reference CSV
                </button>
            </div>
            <label className="mt-4 flex items-center gap-3 cursor-pointer w-fit">
                <input type="checkbox" checked={aiMatch} onChange={(e) => setAiMatch(e.target.checked)} className="accent-brand-olive w-4 h-4" data-testid="bulk-ai-match-toggle" />
                <span className="text-sm"><span className="text-brand-gold">AI auto-match</span> files whose names don't match (identifies items by photo)</span>
            </label>
            <label
                data-testid="bulk-drop-zone"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); submit(collectFiles(e.dataTransfer.files)); }}
                className={`mt-4 block border-2 border-dashed rounded-sm py-12 px-6 text-center cursor-pointer transition-colors ${
                    dragOver ? "border-brand-olive bg-brand-olive/10" : "border-brand-line hover:border-brand-gold"
                }`}
            >
                <Upload className="mx-auto text-brand-gold mb-3" size={32} strokeWidth={1.5} />
                <p className="text-brand-cream">Drop your folder, images, or a ZIP here</p>
                <p className="text-xs text-brand-mute mt-2">JPG · PNG · WEBP · GIF · max 5 MB per image · ZIP up to 50 MB</p>
                <div className="flex flex-wrap gap-2 justify-center mt-5">
                    <button type="button" onClick={pick(folderRef)} className="btn-primary text-xs">Choose folder</button>
                    <button type="button" onClick={pick(imgRef)} className="btn-ghost text-xs">Choose images</button>
                    <button type="button" onClick={pick(zipRef)} className="btn-ghost text-xs">Upload ZIP</button>
                </div>
                <input ref={imgRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => submit(collectFiles(e.target.files))} />
                <input ref={folderRef} type="file" multiple webkitdirectory="" directory="" className="hidden" onChange={(e) => submit(collectFiles(e.target.files))} />
                <input ref={zipRef} type="file" accept=".zip,application/zip" className="hidden" onChange={(e) => submit(collectFiles(e.target.files))} />
            </label>
            {uploading && (
                <div className="mt-4 flex items-center gap-3 text-sm text-brand-gold">
                    <div className="w-4 h-4 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
                    <span>Uploading and matching…</span>
                </div>
            )}
            {results && <Results results={results} />}
        </div>
    );
}
